import { useState, useEffect, useMemo } from 'react'
import API from '../API/API'
import { useAlerts } from '../contexts/AlertContext'
import logger from '../utils/logger'
import { validateHelmetId, mapToDisambiguationOption, mapToHelmetInfo, mapDisambiguationToHelmetInfo } from '../utils/helmetLookup'
import { HelmetInfo, DisambiguationOption, ValidationResult, Organization, ConvertibleValidation } from '../types/helmetLookup'

interface UseHelmetLookupParams {
  watchedHelmetId: string
  watchedTargetOrgID: string
  watchedTargetTeamID: string
  setValue: (name: any, value: any, options?: any) => void
  onLookupStart?: () => void
}

export const useHelmetLookup = ({ watchedHelmetId, watchedTargetOrgID, watchedTargetTeamID, setValue, onLookupStart }: UseHelmetLookupParams) => {
  const [isLookingUp, setIsLookingUp] = useState(false)
  const [isLoadingOrgs, setIsLoadingOrgs] = useState(false)
  const [isLoadingTargetTeams, setIsLoadingTargetTeams] = useState(false)
  const [isValidatingConvertible, setIsValidatingConvertible] = useState(false)
  const [convertibleValidation, setConvertibleValidation] = useState<ConvertibleValidation | null>(null)

  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [helmetInfo, setHelmetInfo] = useState<HelmetInfo | null>(null)
  const [multipleResults, setMultipleResults] = useState<DisambiguationOption[]>([])
  const [availableTargetTeams, setAvailableTargetTeams] = useState<any[]>([])

  const [validationResult, setValidationResult] = useState<ValidationResult>({ valid: false, type: null, format: null })
  const [showDisambiguation, setShowDisambiguation] = useState(false)
  const [showHelmetInfo, setShowHelmetInfo] = useState(false)
  const [error, setError] = useState<string>('')

  const { addAlert } = useAlerts()

  // Memoized computations for performance
  const availableTargetOrgs = useMemo(() => 
    organizations.filter(org => 
      helmetInfo ? org.orgID !== helmetInfo.sourceOrgID : true
    ), [organizations, helmetInfo]
  )

  const disambiguationOptions = useMemo(() => 
    multipleResults.map((option, index) => ({
      ...option,
      key: `${option.helmetId}-${option.sourceOrgID}-${index}`
    })), [multipleResults]
  )

  // Real-time validation effect
  useEffect(() => {
    const result = validateHelmetId(watchedHelmetId)
    setValidationResult(result)
    
    // Clear previous results when input changes
    if (helmetInfo || multipleResults.length > 0) {
      setHelmetInfo(null)
      setMultipleResults([])
      setShowHelmetInfo(false)
      setShowDisambiguation(false)
      setError('')
    }
  }, [watchedHelmetId])

  // Load organizations on component mount
  useEffect(() => {
    loadOrganizations()
  }, [])

  // Load target teams when target organization changes
  useEffect(() => {
    if (watchedTargetOrgID && watchedTargetOrgID !== '') {
      loadTargetTeams(parseInt(watchedTargetOrgID))
    } else {
      setAvailableTargetTeams([])
      setValue('targetTeamID', '')
    }
  }, [watchedTargetOrgID])

  // Validate convertible helmets when target team is selected
  useEffect(() => {
    const validateConvertible = async () => {
      if (helmetInfo?.features?.toLowerCase() !== 'convertible' || !watchedTargetTeamID) {
        setConvertibleValidation(null)
        return
      }

      try {
        setIsValidatingConvertible(true)
        const teamID = parseInt(watchedTargetTeamID)
        const rosterResponse = await API.GetRoster(teamID)
        const players = rosterResponse.data || []

        const matchingPlayer = players.find((player: any) =>
          player.firstName?.toUpperCase() === 'RIDDELL' &&
          player.lastName?.toUpperCase() === (helmetInfo.helmetHatSize || '').toUpperCase()
        )

        if (matchingPlayer) {
          setConvertibleValidation({
            isConvertible: true,
            requiredPlayer: {
              playerID: matchingPlayer.playerID,
              playerName: `${matchingPlayer.firstName} ${matchingPlayer.lastName}`,
              firstName: matchingPlayer.firstName,
              lastName: matchingPlayer.lastName,
              jerseyNumber: matchingPlayer.jerseyNumber,
              positionID: matchingPlayer.positionID,
              playerUnitID: matchingPlayer.playerUnitID
            },
            isValid: true,
            errorMessage: null
          })
        } else {
          setConvertibleValidation({
            isConvertible: true,
            requiredPlayer: null,
            isValid: false,
            errorMessage: `This is a Convertible helmet. The target organization must have a player named "RIDDELL ${helmetInfo.helmetHatSize}" to receive this helmet.`
          })
        }
      } catch (error: any) {
        logger.error('Failed to validate convertible helmet', { module: 'useHelmetLookup', data: { error: error.message } })
        setConvertibleValidation({
          isConvertible: true,
          requiredPlayer: null,
          isValid: false,
          errorMessage: 'Failed to validate convertible helmet requirements. Please try again.'
        })
      } finally {
        setIsValidatingConvertible(false)
      }
    }

    validateConvertible()
  }, [watchedTargetTeamID, helmetInfo])

  const loadOrganizations = async () => {
    try {
      setIsLoadingOrgs(true)
      const response = await API.GetOrgs()
      if (response.success && response.data) {
        setOrganizations(response.data)
      } else {
        throw new Error('Failed to load organizations')
      }
    } catch (error: any) {
      logger.error('Failed to load organizations', { module: 'HelmetLookupTool', data: { error: error.message } })
      setError('Unable to load organizations. Please refresh the page.')
      addAlert('error', 'Failed to load organizations')
    } finally {
      setIsLoadingOrgs(false)
    }
  }

  const loadTargetTeams = async (orgID: number) => {
    try {
      setIsLoadingTargetTeams(true)
      const response = await API.GetOrgTeams(orgID.toString())
      if (response.success && response.data) {
        setAvailableTargetTeams(response.data)
        
        // Auto-select team if there's only one team available
        if (response.data.length === 1) {
          setValue('targetTeamID', response.data[0].teamID.toString())
        } else {
          // Clear team selection if multiple teams available
          setValue('targetTeamID', '')
        }
      } else {
        setAvailableTargetTeams([])
        setValue('targetTeamID', '')
      }
    } catch (error: any) {
      logger.error('Failed to load target teams', { module: 'HelmetLookupTool', data: { error: error.message, orgID } })
      setAvailableTargetTeams([])
      setValue('targetTeamID', '')
    } finally {
      setIsLoadingTargetTeams(false)
    }
  }

  const handleHelmetLookup = async () => {
    if (!validationResult.valid) {
      setError('Please enter a valid helmet ID')
      return
    }

    try {
      setIsLookingUp(true)
      setError('')
      
      // Clear previous transfer success when starting new lookup
      if (onLookupStart) onLookupStart()
      
      // Real API call
      const response = await API.GetHelmetInfo(watchedHelmetId)

      if (response.success && response.data) {
        // Normalize API response shapes (some endpoints return { rowCount, rows })
        const raw = response.data

        // Handle empty result shapes early
        if ((raw && typeof raw.rowCount === 'number' && raw.rowCount === 0) || (Array.isArray(raw) && raw.length === 0) || (raw && Array.isArray(raw.rows) && raw.rows.length === 0)) {
          // No helmets found
          setError('Helmet not found with this ID')
          addAlert('error', 'Helmet lookup returned no results')
          return
        }

        // Coerce into a usable array or object for downstream logic
        const data = Array.isArray(raw) ? raw : (raw.rows && Array.isArray(raw.rows) ? raw.rows : raw)

        // Check if we have multiple results requiring disambiguation
        if (Array.isArray(data)) {
          const disambiguationOpts: DisambiguationOption[] = response.data.map((helmet: any) => 
            mapToDisambiguationOption(helmet, watchedHelmetId)
          )
          
          // Check if all results are from the same organization
          const uniqueOrgs = new Set(disambiguationOpts.map(option => option.sourceOrgID))
          
          if (uniqueOrgs.size === 1) {
            // All results from same organization - pick the best-matching result
            let firstResult = data[0]

            if (validationResult.type === 'rfidCode') {
              const match = data.find((h: any) => (h.rfidBarcode && h.rfidBarcode === watchedHelmetId) || (h.rfidCode && h.rfidCode === watchedHelmetId))
              if (match) firstResult = match
            } else if (validationResult.type === 'rfidLabel') {
              const match = data.find((h: any) => (h.helmetRFIDLabel && h.helmetRFIDLabel === watchedHelmetId) || (h.rfidLabel && h.rfidLabel === watchedHelmetId))
              if (match) firstResult = match
            }

            const helmetData = mapToHelmetInfo(firstResult, watchedHelmetId, validationResult)
            setHelmetInfo(helmetData)
            setShowHelmetInfo(true)
            setShowDisambiguation(false)
          } else {
            // Multiple organizations - show disambiguation
            setMultipleResults(disambiguationOpts)
            setShowDisambiguation(true)
            setShowHelmetInfo(false)
          }
        } else {
          // Single result - display helmet info
          const helmetData = mapToHelmetInfo(data, watchedHelmetId, validationResult)
          setHelmetInfo(helmetData)
          setShowHelmetInfo(true)
          setShowDisambiguation(false)
        }
      } else {
        throw new Error('No helmet data received')
      }
    } catch (error: any) {
      logger.error('Failed to lookup helmet', { module: 'HelmetLookupTool', data: { error: error.message, helmetId: watchedHelmetId } })
      
      if (error.response?.status === 404) {
        setError('Helmet not found with this ID')
      } else if (error.response?.status === 403) {
        setError('Insufficient permissions to access helmet information')
      } else {
        setError('Unable to retrieve helmet information. Please try again.')
      }
      
      addAlert('error', 'Helmet lookup failed')
    } finally {
      setIsLookingUp(false)
    }
  }

  const handleDisambiguationSelect = (option: DisambiguationOption) => {
    const selectedHelmetInfo = mapDisambiguationToHelmetInfo(option, watchedHelmetId, validationResult)
    setHelmetInfo(selectedHelmetInfo)
    setShowHelmetInfo(true)
    setShowDisambiguation(false)
    setMultipleResults([])
  }

  const resetLookupState = () => {
    setHelmetInfo(null)
    setMultipleResults([])
    setAvailableTargetTeams([])
    setValidationResult({ valid: false, type: null, format: null })
    setShowDisambiguation(false)
    setShowHelmetInfo(false)
    setError('')
    setIsLookingUp(false)
    setIsLoadingTargetTeams(false)
    setConvertibleValidation(null)
  }

  return {
    // Loading states
    isLookingUp,
    isLoadingOrgs,
    isLoadingTargetTeams,
    isValidatingConvertible,
    // Data states
    organizations,
    helmetInfo,
    multipleResults,
    availableTargetTeams,
    availableTargetOrgs,
    disambiguationOptions,
    convertibleValidation,
    // UI states
    validationResult,
    showDisambiguation,
    showHelmetInfo,
    error,
    // Actions
    handleHelmetLookup,
    handleDisambiguationSelect,
    loadTargetTeams,
    resetLookupState,
    // Setters needed by transfer
    setHelmetInfo,
    setShowHelmetInfo,
    setMultipleResults,
    setShowDisambiguation,
    setAvailableTargetTeams,
    setError
  }
}
